/**
 * LlmAllowlistPanel — admin allow-list LLM pro Codable (tenant + projekt).
 *
 * UX jako všude jinde: PriorityList drag&drop, toggle zapnuto, **pořadí = priorita**
 * (první zapnutý = výchozí v chatu). Žádná samostatná položka „výchozí model“.
 * Explicitní **Uložit** (draft v paměti, ne auto-save po každém kliku).
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { api } from '../../../lib/api';
import { Button, Card, CardContent, PriorityList, Select, Toggle } from '../../../components/ui';
import type { WidgetRenderProps } from '../../../widgets/types';

interface Tenant {
  id: string;
  slug: string;
  name: string;
}
interface CodableProject {
  id: string;
  slug: string;
  name: string;
  tenant_id: string | null;
}
interface LlmProfileSummary {
  id: string;
  label: string;
  model?: string;
  backend?: string;
  codable_enabled?: boolean;
  enabled?: boolean;
}
interface AllowlistItem {
  id: string;
  enabled: boolean;
  priority: number;
  label: string;
  subtitle: string;
}

function useCodableCandidates() {
  return useQuery({
    queryKey: ['codable-llm-candidates'],
    queryFn: async () => {
      const r = await api.mcp<{ profiles: LlmProfileSummary[] }>('llm-profiles.list', {});
      // Jen codable_enabled z registru — a jen zapnuté profily (enabled !== false)
      return (r.profiles ?? []).filter(
        (p) => p.codable_enabled === true && p.enabled !== false,
      );
    },
    staleTime: 30_000,
  });
}

/** Seřadí kandidáty: nejdřív allowlist v daném pořadí, pak zbytek (vypnuté). */
function buildItems(
  candidates: LlmProfileSummary[],
  allowedOrdered: string[],
): AllowlistItem[] {
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const allowedSet = new Set(allowedOrdered);
  const items: AllowlistItem[] = [];
  let pri = 0;
  for (const id of allowedOrdered) {
    const p = byId.get(id);
    if (!p) continue;
    items.push({
      id: p.id,
      enabled: true,
      priority: pri++,
      label: p.label,
      subtitle: `${p.backend ?? 'openai'} · ${p.model ?? '(no model)'}`,
    });
  }
  for (const p of candidates) {
    if (allowedSet.has(p.id)) continue;
    items.push({
      id: p.id,
      enabled: false,
      priority: pri++,
      label: p.label,
      subtitle: `${p.backend ?? 'openai'} · ${p.model ?? '(no model)'}`,
    });
  }
  return items;
}

function draftToProfileIds(items: AllowlistItem[]): string[] {
  // Jen zapnuté, v aktuálním pořadí (priority list už drží order)
  return items.filter((i) => i.enabled).map((i) => i.id);
}

export function LlmAllowlistPanel(_: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: tenantsData } = useQuery({
    queryKey: ['codable-tenants'],
    queryFn: () => api.mcp<{ ok: boolean; tenants: Tenant[] }>('mesh.tenants', { action: 'list' }),
    staleTime: 60_000,
  });
  const { data: projectsData } = useQuery({
    queryKey: ['codable-projects-admin'],
    queryFn: () => api.mcp<{ ok: boolean; projects: CodableProject[] }>('codable.project_list', {}),
    staleTime: 30_000,
  });
  const { data: candidates } = useCodableCandidates();

  const [tenantIdSel, setTenantIdSel] = useState('');
  const [projectId, setProjectId] = useState('');

  const tenantList = tenantsData?.tenants ?? [];
  const projectList = projectsData?.projects ?? [];
  const tenantId = tenantIdSel || tenantList[0]?.id || '';

  const { data: tenantAllowlist } = useQuery({
    queryKey: ['codable-tenant-allowlist', tenantId],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        profile_ids: string[];
        byok_allowed: boolean;
      }>('codable.llm_tenant_allowlist_get', { tenant_id: tenantId }),
    enabled: !!tenantId,
  });

  const { data: projectAllowlist } = useQuery({
    queryKey: ['codable-project-allowlist', projectId],
    queryFn: () =>
      api.mcp<{ ok: boolean; profile_ids: string[]; effective_ids: string[] | null }>(
        'codable.llm_project_allowlist_get',
        { project_id: projectId },
      ),
    enabled: !!projectId,
  });

  // Draft stavy — explicitní Uložit
  const [tenantDraft, setTenantDraft] = useState<AllowlistItem[]>([]);
  const [projectDraft, setProjectDraft] = useState<AllowlistItem[]>([]);
  const [byokDraft, setByokDraft] = useState(true);
  const [tenantDirty, setTenantDirty] = useState(false);
  const [projectDirty, setProjectDirty] = useState(false);
  const [savingTenant, setSavingTenant] = useState(false);
  const [savingProject, setSavingProject] = useState(false);

  // Hydrate tenant draft z serveru
  useEffect(() => {
    if (!candidates) return;
    const ids = tenantAllowlist?.profile_ids ?? [];
    setTenantDraft(buildItems(candidates, ids));
    setByokDraft(tenantAllowlist?.byok_allowed ?? true);
    setTenantDirty(false);
  }, [candidates, tenantAllowlist, tenantId]);

  // Hydrate project draft
  useEffect(() => {
    if (!candidates || !projectId) {
      setProjectDraft([]);
      setProjectDirty(false);
      return;
    }
    const ids = projectAllowlist?.profile_ids ?? [];
    // Projekt smí jen zúžit tenant — kandidáti = tenant enabled (nebo všichni pokud tenant unrestricted)
    const tenantIds = tenantAllowlist?.profile_ids ?? [];
    const cand = tenantIds.length > 0
      ? candidates.filter((c) => tenantIds.includes(c.id))
      : candidates;
    setProjectDraft(buildItems(cand, ids));
    setProjectDirty(false);
  }, [candidates, projectAllowlist, projectId, tenantAllowlist?.profile_ids]);

  const patchTenant = (next: AllowlistItem[]) => {
    setTenantDraft(next.map((it, i) => ({ ...it, priority: i })));
    setTenantDirty(true);
  };
  const patchProject = (next: AllowlistItem[]) => {
    setProjectDraft(next.map((it, i) => ({ ...it, priority: i })));
    setProjectDirty(true);
  };

  const saveTenant = async () => {
    if (!tenantId) return;
    setSavingTenant(true);
    try {
      await api.mcp('codable.llm_tenant_allowlist_set', {
        tenant_id: tenantId,
        profile_ids: draftToProfileIds(tenantDraft),
        byok_allowed: byokDraft,
        // Neposílat default_profile_id — priorita = pořadí
      });
      qc.invalidateQueries({ queryKey: ['codable-tenant-allowlist', tenantId] });
      qc.invalidateQueries({ queryKey: ['codable-llm-profiles'] });
      setTenantDirty(false);
      toast.success(t('codableLlm.tenantSaved'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSavingTenant(false);
    }
  };

  const saveProject = async () => {
    if (!projectId) return;
    setSavingProject(true);
    try {
      await api.mcp('codable.llm_project_allowlist_set', {
        project_id: projectId,
        profile_ids: draftToProfileIds(projectDraft),
      });
      // Vyčistit legacy chat_profile default (priorita = pořadí)
      try {
        await api.mcp('codable.project_chat_profile_set', {
          project_id: projectId,
          chat_profile: '',
        });
      } catch { /* optional */ }
      qc.invalidateQueries({ queryKey: ['codable-project-allowlist', projectId] });
      qc.invalidateQueries({ queryKey: ['codable-llm-profiles'] });
      setProjectDirty(false);
      toast.success(t('codableLlm.projectSaved'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSavingProject(false);
    }
  };

  const selectedProject = projectList.find((p) => p.id === projectId);
  const candCount = candidates?.length ?? 0;
  const defaultMark = t('codableLlm.defaultFirst');

  return (
    <div className='space-y-4'>
      <p className='text-[11px] text-gray-500'>
        {t('codableLlm.intro')}
      </p>

      <Card>
        <CardContent>
          <div className='grid grid-cols-2 gap-3'>
            <label className='block'>
              <span className='text-[10px] text-gray-600 mb-1 block'>{t('codableLlm.tenant')}</span>
              <Select size='sm' value={tenantId} onChange={(e) => setTenantIdSel(e.target.value)}>
                {tenantList.map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
              </Select>
            </label>
            <label className='block'>
              <span className='text-[10px] text-gray-600 mb-1 block'>
                {t('codableLlm.projectNarrow')}
              </span>
              <Select size='sm' value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value=''>{t('codableLlm.noProjectOption')}</option>
                {projectList.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || p.slug}</option>
                ))}
              </Select>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <div className='flex items-center justify-between mb-1 gap-2'>
            <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
              {t('codableLlm.tenantSection')}
            </h3>
            <div className='flex items-center gap-3'>
              <Toggle
                value={byokDraft}
                label={t('codableLlm.byok')}
                onSave={(v) => {
                  setByokDraft(v);
                  setTenantDirty(true);
                }}
              />
              <Button
                size='sm'
                icon={<Save size={14} />}
                loading={savingTenant}
                disabled={!tenantDirty || savingTenant}
                onClick={() => void saveTenant()}
              >
                {t('codableLlm.save')}
              </Button>
            </div>
          </div>
          <p className='text-[11px] text-gray-500 mb-3'>
            {t('codableLlm.registryOnly')}{' '}
            {candCount === 0
              ? t('codableLlm.tenantMetaEmpty')
              : t('codableLlm.tenantMetaCount', {
                cand: candCount,
                on: draftToProfileIds(tenantDraft).length,
              })}
            {tenantDirty && (
              <span className='text-amber-400 ml-2'>{t('codableLlm.unsaved')}</span>
            )}
          </p>
          {tenantDraft.length === 0
            ? (
              <div className='text-sm text-gray-500 text-center py-6'>
                {t('codableLlm.noCodableProfiles')}
              </div>
            )
            : (
              <PriorityList<AllowlistItem>
                items={tenantDraft}
                getTitle={(i) => i.label}
                getSubtitle={(i) => (
                  <span className='font-mono'>
                    {i.subtitle}
                    {i.enabled && i.priority === 0
                      ? defaultMark
                      : i.enabled
                      ? ` · #${i.priority + 1}`
                      : ''}
                  </span>
                )}
                renderEditor={() => null}
                allowExpand={false}
                allowDelete={false}
                showDragHandle
                onSave={() => {}}
                onDelete={() => {}}
                onReorder={(ids) => {
                  const map = new Map(tenantDraft.map((x) => [x.id, x]));
                  patchTenant(ids.map((id) => map.get(id)!).filter(Boolean));
                }}
                onToggle={(id, enabled) => {
                  patchTenant(
                    tenantDraft.map((x) => (x.id === id ? { ...x, enabled } : x)),
                  );
                }}
                emptyMessage={t('codableLlm.emptyProfiles')}
              />
            )}
        </CardContent>
      </Card>

      {projectId && (
        <Card>
          <CardContent>
            <div className='flex items-center justify-between mb-1 gap-2'>
              <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                {t('codableLlm.projectSection', {
                  name: selectedProject?.name || selectedProject?.slug || '',
                })}
              </h3>
              <Button
                size='sm'
                icon={<Save size={14} />}
                loading={savingProject}
                disabled={!projectDirty || savingProject}
                onClick={() => void saveProject()}
              >
                {t('codableLlm.save')}
              </Button>
            </div>
            <p className='text-[11px] text-gray-500 mb-3'>
              {t('codableLlm.projectMeta')}{' '}
              {projectAllowlist?.effective_ids === null
                ? t('codableLlm.effectiveUnlimited')
                : t('codableLlm.effectiveCount', {
                  n: projectAllowlist?.effective_ids?.length ?? 0,
                })}
              {projectDirty && (
                <span className='text-amber-400 ml-2'>{t('codableLlm.unsaved')}</span>
              )}
            </p>
            {projectDraft.length === 0
              ? (
                <div className='text-sm text-gray-500 text-center py-6'>
                  {t('codableLlm.noProjectCandidates')}
                </div>
              )
              : (
                <PriorityList<AllowlistItem>
                  items={projectDraft}
                  getTitle={(i) => i.label}
                  getSubtitle={(i) => (
                    <span className='font-mono'>
                      {i.subtitle}
                      {i.enabled && i.priority === 0
                        ? defaultMark
                        : i.enabled
                        ? ` · #${i.priority + 1}`
                        : ''}
                    </span>
                  )}
                  renderEditor={() => null}
                  allowExpand={false}
                  allowDelete={false}
                  showDragHandle
                  onSave={() => {}}
                  onDelete={() => {}}
                  onReorder={(ids) => {
                    const map = new Map(projectDraft.map((x) => [x.id, x]));
                    patchProject(ids.map((id) => map.get(id)!).filter(Boolean));
                  }}
                  onToggle={(id, enabled) => {
                    patchProject(
                      projectDraft.map((x) => (x.id === id ? { ...x, enabled } : x)),
                    );
                  }}
                  emptyMessage={t('codableLlm.emptyProfiles')}
                />
              )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
